import {
  convertLength,
  LENGTH_SYMBOLS,
  type LengthUnitId,
} from "@/lib/tool-kit/geometry/units";
import {
  formatStyled,
  type NumberStyle,
  parseRational,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Typed measurements, read exactly and converted into one length unit.
 *
 * A reader can give every measurement in the same unit, or mix them (2 ft by 30 in):
 * each is converted to the unit the answer is written in with an exact rational factor,
 * so the exact form of the answer survives the conversion.
 */

export interface UnitSetup {
  /** Give each measurement its own unit. */
  mixed: boolean;
  /** The one unit used when `mixed` is off. */
  unit: LengthUnitId;
  /** A unit per measurement, used when `mixed` is on. */
  units: Record<string, LengthUnitId>;
  /** The unit the answer is written in when `mixed` is on. */
  resultUnit: LengthUnitId;
}

/** The unit the answer is expressed in. */
export function answerUnit(setup: UnitSetup): LengthUnitId {
  return setup.mixed ? setup.resultUnit : setup.unit;
}

function unitOf(setup: UnitSetup, id: string): LengthUnitId {
  return setup.mixed ? (setup.units[id] ?? setup.unit) : setup.unit;
}

export type ReadError = "enter" | "invalid";

export interface ReadMeasurements {
  ok: true;
  /** Each measurement as an exact rational in the answer unit. */
  lengths: Record<string, Rational>;
  /** Each measurement as typed with its own unit: "6 cm". */
  labels: Record<string, string>;
  /** Each measurement as a float in the answer unit, for drawing. */
  numbers: Record<string, number>;
}

export function readMeasurements(
  ids: readonly string[],
  texts: Record<string, string>,
  setup: UnitSetup,
  mark: "." | ",",
  style: NumberStyle
): ReadMeasurements | { ok: false; error: ReadError } {
  const target = answerUnit(setup);
  const lengths: Record<string, Rational> = {};
  const labels: Record<string, string> = {};
  const numbers: Record<string, number> = {};
  if (ids.some((id) => (texts[id] ?? "").trim() === "")) {
    return { ok: false, error: "enter" };
  }
  for (const id of ids) {
    const value = parseRational(texts[id] ?? "");
    if (value === null) {
      return { ok: false, error: "invalid" };
    }
    const from = unitOf(setup, id);
    lengths[id] = convertLength(value, from, target);
    labels[id] = `${formatStyled(value, style, mark)} ${LENGTH_SYMBOLS[from]}`;
    numbers[id] = toNumberR(lengths[id]);
  }
  return { ok: true, lengths, labels, numbers };
}

const DECIMAL_TEXT = /[.,]/;

/** Decimals if the reader typed any decimal, fractions otherwise (as the slope tool does). */
export function styleOfTexts(texts: readonly string[]): NumberStyle {
  return texts.some((text) => DECIMAL_TEXT.test(text)) ? "decimal" : "fraction";
}
